import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { FormControl, FormLabel, FormErrorMessage, InputGroup, Input, InputRightElement, IconButton, Button, Checkbox, Alert, AlertIcon, Text, Link, Box, useToast } from '@chakra-ui/react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useFormik } from 'formik';

import AuthShell from '../components/AuthShell';
import GoogleButton from '../components/GoogleButton';
import { useUserContext } from '../contexts/UserContext';
import LoginValidations from '../validations/LoginValidations';
import { Login as LogIn } from '../services/AuthServices';
import { whatsappLink } from '../config/store';

const Login = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { currentUser, login } = useUserContext();
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState('');

  // Pages that send people here pass { from } so we can return them afterwards
  const from = location.state?.from;

  useEffect(() => {
    if (currentUser) navigate(from || '/', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { values, errors, touched, handleSubmit, handleChange, handleBlur, isSubmitting, setSubmitting } = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: LoginValidations,
    onSubmit: async (values) => {
      setError('');
      try {
        const result = await LogIn(values.email.trim(), values.password);
        const user = result.data.currentUser;
        if (!user) {
          setError('Wrong email or password. Please try again.');
          setSubmitting(false);
          return;
        }
        login(user, result.data.token, remember);
        toast({ title: `Welcome back, ${user.firstName}!`, status: 'success', duration: 1800, position: 'top' });
        navigate(user.admin ? '/admin' : (from || '/'), { replace: true });
      } catch (e) {
        setError('Could not reach the store. Check your connection and try again.');
        setSubmitting(false);
      }
    }
  });

  return (
    <AuthShell
      title='Log in'
      subtitle='Welcome back to Raano.'
      footer={<Text color='blackAlpha.700' >New to Raano? <Link as={RouterLink} to='/register' state={{ from }} color='raano.black' fontWeight={700} textDecoration='underline' >Create an account</Link></Text>}
    >
      <GoogleButton
        text='continue_with'
        onSuccess={(data) => {
          login(data.currentUser, data.token, true);
          toast({ title: data.created ? `Welcome to Raano, ${data.currentUser.firstName}!` : `Welcome back, ${data.currentUser.firstName}!`, status: 'success', duration: 1800, position: 'top' });
          navigate(data.currentUser.admin ? '/admin' : (from || '/'), { replace: true });
        }}
        onError={setError}
      />
      <Box as='form' onSubmit={handleSubmit} noValidate >
        {error && <Alert status='error' borderRadius={10} mb={4} fontSize={14} ><AlertIcon />{error}</Alert>}

        <FormControl isInvalid={touched.email && !!errors.email} mb={4} >
          <FormLabel fontSize={14} mb={1} >Email</FormLabel>
          <Input name='email' type='email' inputMode='email' autoComplete='email' autoCapitalize='none' placeholder='you@example.com' size='lg' fontSize={16} value={values.email} onChange={handleChange} onBlur={handleBlur} />
          <FormErrorMessage>{errors.email}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={touched.password && !!errors.password} mb={3} >
          <FormLabel fontSize={14} mb={1} >Password</FormLabel>
          <InputGroup size='lg' >
            <Input name='password' type={show ? 'text' : 'password'} autoComplete='current-password' placeholder='Your password' fontSize={16} value={values.password} onChange={handleChange} onBlur={handleBlur} />
            <InputRightElement>
              <IconButton size='sm' variant='ghost' borderRadius='full' aria-label={show ? 'Hide password' : 'Show password'} icon={show ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />} onClick={() => setShow(!show)} />
            </InputRightElement>
          </InputGroup>
          <FormErrorMessage>{errors.password}</FormErrorMessage>
        </FormControl>

        <Box display='flex' justifyContent='space-between' alignItems='center' mb={6} >
          <Checkbox isChecked={remember} onChange={(e) => setRemember(e.target.checked)} colorScheme='blackAlpha' >
            <Text fontSize={14} >Keep me logged in</Text>
          </Checkbox>
          <Link href={whatsappLink('Hi Raano, I forgot my password. Please help me reset it.')} isExternal fontSize={14} color='raano.black' fontWeight={600} textDecoration='underline' >Forgot password?</Link>
        </Box>

        <Button type='submit' width='100%' variant='ink' height='48px' fontSize='17px' isLoading={isSubmitting} loadingText='Logging in…' >Log in</Button>
      </Box>
    </AuthShell>
  )
}

export default Login;
